/**
 * Aligns WebSocket-driven list updates with Conversations::PermissionFilterService
 * for Supervisor / Back-Up (team-scoped roles)
 * and Ejecutivo / Asistente (assignee-scoped roles).
 */

const TEAM_SCOPED_ROLES = ['Supervisor'];
const ASSIGNEE_SCOPED_ROLES = ['Ejecutivo', 'Asistente'];

export function getTeamScopedRoleName(customRoleName) {
  if (!customRoleName || typeof customRoleName !== 'string') return null;
  const trimmed = customRoleName.trim();
  return TEAM_SCOPED_ROLES.includes(trimmed) ? trimmed : null;
}

export function getAssigneeScopedRoleName(customRoleName) {
  if (!customRoleName || typeof customRoleName !== 'string') return null;
  const trimmed = customRoleName.trim();
  return ASSIGNEE_SCOPED_ROLES.includes(trimmed) ? trimmed : null;
}

export function getConversationTeamId(conversation) {
  if (!conversation) return null;
  if (conversation.team_id != null && conversation.team_id !== '') {
    return Number(conversation.team_id);
  }
  const metaTeamId = conversation.meta?.team?.id;
  if (metaTeamId != null && metaTeamId !== '') {
    return Number(metaTeamId);
  }
  return null;
}

function teamsStillLoading(rootState) {
  return Boolean(rootState.teams?.uiFlags?.isFetching);
}

/**
 * @param {object} conversation — payload from API or Action Cable (push_event_data shape)
 * @param {object} rootGetters — Vuex root getters
 * @param {object} [rootState] — optional; if teams are still loading, skip filtering
 * @returns {boolean}
 */
export function isTeamScopedConversationVisible(
  conversation,
  rootGetters,
  rootState = null
) {
  const account = rootGetters.getCurrentAccount;
  const roleName = account?.custom_role?.name;
  if (!getTeamScopedRoleName(roleName)) return true;

  if (rootState && teamsStillLoading(rootState)) {
    return true;
  }

  const myTeams = rootGetters['teams/getMyTeams'] || [];
  const myTeamIds = myTeams.map(t => t.id);
  if (myTeamIds.length === 0) {
    return false;
  }

  const teamId = getConversationTeamId(conversation);
  if (!teamId) return false;
  return myTeamIds.includes(teamId);
}

/**
 * Ejecutivo / Asistente: only see conversations assigned to them
 * AND belonging to one of their teams (mirroring PermissionFilterService).
 */
export function isAssigneeScopedConversationVisible(
  conversation,
  rootGetters,
  rootState = null
) {
  const account = rootGetters.getCurrentAccount;
  const roleName = account?.custom_role?.name;
  if (!getAssigneeScopedRoleName(roleName)) return true;

  const currentUserId = rootGetters.getCurrentUser?.id;
  const assigneeId =
    conversation.meta?.assignee?.id ?? conversation.assignee_id;

  if (assigneeId !== currentUserId) return false;

  if (rootState && teamsStillLoading(rootState)) return true;

  const myTeams = rootGetters['teams/getMyTeams'] || [];
  const myTeamIds = myTeams.map(t => t.id);
  if (myTeamIds.length === 0) return true;

  const teamId = getConversationTeamId(conversation);
  if (!teamId) return false;
  return myTeamIds.includes(teamId);
}
